import { Braces } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { describeDataDescription, parseDataDescriptionInput } from '../builderModel';
import { Field } from '../builderUi';

const PLACEHOLDER = `{
  "version": 1,
  "sensitivity": "internal",
  "claims": ["quote", "count", "entities", "grouping"],
  "guidance": "What the source is and how to read it well.",
  "budget": { "callsPerSession": 200 },
  "operations": [
    { "operationId": "search", "role": "search", "parameters": { "type": "object", "properties": {} },
      "records": { "path": "rows", "id": "id", "time": "occurredAt", "link": "url", "text": "text" } }
  ]
}`;

/**
 * The data description: what the integration's data is for an agent that calls it as a tool. Studio stores
 * it with the integration and freezes it with a release like the copy; the work-host reads it and builds the
 * agent's tools from it. Optional: without one, agents see the operations and guidance but get no tools.
 *
 * The editor holds JSON text. It is checked here for shape (an object, under the size cap) before the step
 * saves; the schema itself belongs to the work-host (`backend/work-host/docs/data-description.md`).
 */
export function DataDescriptionSection({
  text,
  saved,
  readOnly,
  onChange,
}: {
  text: string;
  saved: Record<string, unknown> | undefined;
  readOnly: boolean;
  onChange: (text: string) => void;
}) {
  const parsed = parseDataDescriptionInput(text);
  const problem = 'error' in parsed ? parsed.error : undefined;
  const canFormat = !readOnly && !problem && 'value' in parsed && parsed.value !== null && text !== JSON.stringify(parsed.value, null, 2);

  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="flex flex-wrap items-start justify-between gap-3 bg-card/40 px-4 py-3">
        <div>
          <div className="text-sm font-semibold">Data description</div>
          <div className="text-xs text-muted-foreground">
            What the data is for an agent that calls this integration as a tool: each operation&apos;s role, where its records are and which
            fields are the id, time, link, text and entity key, the claim types it supports, guidance and a call budget. The work-host builds
            the agent&apos;s tools from it; Studio only stores it.
          </div>
        </div>
        <div className="text-xs text-muted-foreground" data-testid="data-description-summary">
          {describeDataDescription(saved)}
        </div>
      </div>
      <div className="space-y-2 px-4 py-4">
        <Field
          id="data-description"
          label="JSON"
          optional
          hint={
            <>
              The shape is the work-host&apos;s: <code className="font-mono text-xs">backend/work-host/docs/data-description.md</code>, with customer
              voice worked out. Leave it empty to store none; clear it to remove the stored one.
            </>
          }
        >
          <Textarea
            id="data-description"
            value={text}
            disabled={readOnly}
            onChange={(event) => onChange(event.target.value)}
            placeholder={PLACEHOLDER}
            className="min-h-64 font-mono text-xs"
            spellCheck={false}
            aria-invalid={Boolean(problem)}
            aria-describedby={problem ? 'data-description-problem' : undefined}
          />
        </Field>
        <div className="flex items-start justify-between gap-3">
          {problem ? (
            <p id="data-description-problem" role="alert" className="text-xs text-destructive">
              {problem}
            </p>
          ) : (
            <span />
          )}
          {canFormat ? (
            <Button variant="ghost" size="sm" onClick={() => onChange(JSON.stringify((parsed as { value: Record<string, unknown> }).value, null, 2))}>
              <Braces className="mr-2 h-3.5 w-3.5" />
              Format
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
